import { Brain, Clock, MessageCircleQuestion, Square } from "lucide-react";
import type { ReactNode } from "react";

import type { JobActivity, JobStatus } from "@/lib/types";

import { ActivityTimeline } from "./ActivityTimeline";

export function LiveActivity({
  activity,
  status,
  awaitingAnswer = false,
  children,
}: {
  activity?: JobActivity;
  status: JobStatus;
  awaitingAnswer?: boolean;
  children?: ReactNode;
}) {
  const interrupted = status === "cancelled" || status === "failed";
  const queued = status === "pending";
  const state = interrupted ? "interrupted" : awaitingAnswer ? "paused" : "running";
  const Icon = interrupted
    ? Square
    : awaitingAnswer
      ? MessageCircleQuestion
      : queued
        ? Clock
        : Brain;
  const label = interrupted
    ? "Activity interrupted"
    : awaitingAnswer
      ? "Waiting for your answer"
      : queued
        ? "Queued…"
        : "Working…";

  return (
    <div className="chat-bubble border-cyber-magenta/30 bg-cyber-magenta/5 min-w-0 rounded-lg border p-3 text-sm">
      <div className="text-cyber-magenta flex items-center justify-between gap-3">
        <span role="status" className="flex items-center gap-2 text-xs">
          <Icon
            size={16}
            aria-hidden="true"
            className={state === "running" && !queued ? "motion-safe:animate-pulse" : ""}
          />
          {label}
        </span>
        {children}
      </div>
      {activity && activity.steps.length > 0 && (
        <div className="mt-3">
          {activity.truncated && (
            <p className="text-cyber-muted mb-2 text-[11px]">
              Showing recent activity. Earlier entries are omitted from this live preview.
            </p>
          )}
          <ActivityTimeline steps={activity.steps} state={state} />
        </div>
      )}
    </div>
  );
}
